import { resolveExtension } from "../../../Helpers/fileIcon";

export type PreviewKind =
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "docx"
  | "sheet"
  | "pptx"
  | "text"
  | "unsupported";

const normalize = (extension?: string, fileName?: string): string =>
  resolveExtension({
    extension,
    title: fileName,
  });

export function getPreviewKind(extension?: string, fileName?: string): PreviewKind {
  switch (normalize(extension, fileName)) {
    case "pdf":
      return "pdf";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return "image";
    case "mp4":
    case "mov":
      return "video";
    case "mp3":
    case "wav":
      return "audio";
    case "docx":
    case "docm":
    case "dotx":
      return "docx";
    case "xlsx":
    case "xlsm":
    case "xls":
    case "csv":
      return "sheet";
    case "pptx":
    case "pptm":
      return "pptx";
    case "txt":
    case "md":
    case "json":
    case "xml":
    case "html":
    case "htm":
    case "log":
      return "text";
    default:
      return "unsupported";
  }
}

const OFFICE_PARTS: { part: string; kind: PreviewKind }[] = [
  { part: "word/document.xml", kind: "docx" },
  { part: "ppt/presentation.xml", kind: "pptx" },
  { part: "xl/workbook.xml", kind: "sheet" },
];

const indexOfAscii = (bytes: Uint8Array, needle: string): number => {
  const length = needle.length;
  if (length === 0 || bytes.length < length) {
    return -1;
  }
  const first = needle.charCodeAt(0);
  const lastStart = bytes.length - length;
  for (let index = 0; index <= lastStart; index++) {
    if (bytes[index] !== first) {
      continue;
    }
    let matched = true;
    for (let offset = 1; offset < length; offset++) {
      if (bytes[index + offset] !== needle.charCodeAt(offset)) {
        matched = false;
        break;
      }
    }
    if (matched) {
      return index;
    }
  }
  return -1;
};

/** Office files are zip archives. The earliest stored part name wins, so an embedded workbook does not override the host document. */
export function sniffOfficeKind(bytes: ArrayBuffer): "docx" | "pptx" | "sheet" | null {
  const view = new Uint8Array(bytes);
  if (view.length < 4 || view[0] !== 0x50 || view[1] !== 0x4b) {
    return null;
  }

  let earliest = Number.POSITIVE_INFINITY;
  let kind: "docx" | "pptx" | "sheet" | null = null;
  for (const part of OFFICE_PARTS) {
    const at = indexOfAscii(view, part.part);
    if (at >= 0 && at < earliest && (part.kind === "docx" || part.kind === "pptx" || part.kind === "sheet")) {
      earliest = at;
      kind = part.kind;
    }
  }
  return kind;
}

export function previewNeedsBytes(kind: PreviewKind): boolean {
  return kind === "docx" || kind === "sheet" || kind === "pptx" || kind === "text";
}

export function previewOpensNatively(kind: PreviewKind): boolean {
  return kind === "pdf" || kind === "image" || kind === "video" || kind === "audio";
}

export function previewMimeType(kind: PreviewKind, extension?: string, fileName?: string): string {
  const ext = normalize(extension, fileName);
  switch (kind) {
    case "pdf":
      return "application/pdf";
    case "image":
      return ext === "png" ? "image/png" : ext === "gif" ? "image/gif" : ext === "webp" ? "image/webp" : "image/jpeg";
    case "video":
      return ext === "mov" ? "video/quicktime" : "video/mp4";
    case "audio":
      return ext === "wav" ? "audio/wav" : "audio/mpeg";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "sheet":
      return ext === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "text":
      return ext === "html" || ext === "htm" ? "text/html" : "text/plain";
    default:
      return "application/octet-stream";
  }
}
